/**
 * An in-app pop-up that shows a source document instead of downloading it.
 *
 * The content routes are authenticated (and audited server-side as
 * `document_index.document_read`), so the file is fetched with the bearer
 * token and handed to the browser as a blob URL. PDFs open in the browser's
 * own PDF viewer inside the pop-up, at the cited page (`#page=N`); images and
 * plain text render directly. Office files (DOCX, XLSX, PPTX) cannot be
 * displayed by a browser, so the pop-up says so and offers Download.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { downloadFile } from "@/shared/api/integration-browse";

interface ViewRequest {
  /** Authenticated API path of the file (no fragment). */
  path: string;
  /** `#page=N` or "" — applied to PDFs. */
  fragment?: string;
  name: string;
}

type Loaded =
  | { kind: "pdf" | "image"; url: string }
  | { kind: "text"; text: string }
  | { kind: "unsupported"; type: string };

interface Viewer {
  openDocument: (request: ViewRequest) => void;
}

const ViewerContext = createContext<Viewer | null>(null);

const EXTENSION_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  txt: "text/plain",
  csv: "text/csv",
  md: "text/markdown",
};

/** The server may label a file generically; the file name is the tie-breaker. */
function effectiveType(contentType: string, name: string): string {
  const type = contentType.split(";")[0]?.trim().toLowerCase() ?? "";
  if (type && type !== "application/octet-stream") return type;
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_TYPES[ext] ?? type;
}

/** Recognise the common formats from their first bytes ("magic numbers"), so a
 * file stored without an extension or with a generic content type still opens. */
function sniff(bytes: Uint8Array): string | null {
  const starts = (...sig: number[]) => sig.every((b, i) => bytes[i] === b);
  if (starts(0x25, 0x50, 0x44, 0x46)) return "application/pdf"; // %PDF
  if (starts(0x89, 0x50, 0x4e, 0x47)) return "image/png";
  if (starts(0xff, 0xd8, 0xff)) return "image/jpeg";
  return null;
}

function classify(type: string): Loaded["kind"] {
  if (type === "application/pdf") return "pdf";
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("text/") || type === "application/json") return "text";
  return "unsupported";
}

export function DocumentViewerProvider({ children }: PropsWithChildren) {
  const getToken = useAccessToken();
  const [request, setRequest] = useState<ViewRequest | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const objectUrl = useRef<string | null>(null);

  const release = () => {
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = null;
  };

  useEffect(() => {
    if (!request) return;
    let alive = true;
    setLoaded(null);
    setError(null);
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(request.path, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`Could not open the document (HTTP ${res.status}).`);
        const buffer = await res.arrayBuffer();
        const type =
          sniff(new Uint8Array(buffer, 0, Math.min(8, buffer.byteLength))) ??
          effectiveType(res.headers.get("content-type") ?? "", request.name);
        const kind = classify(type);
        if (kind === "text") {
          const text = new TextDecoder().decode(buffer);
          if (alive) setLoaded({ kind, text });
          return;
        }
        if (kind === "unsupported") {
          if (alive) setLoaded({ kind, type });
          return;
        }
        // Re-type the blob so the browser renders it instead of downloading it.
        const blob = new Blob([buffer], { type });
        release();
        objectUrl.current = URL.createObjectURL(blob);
        if (alive) setLoaded({ kind, url: objectUrl.current });
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      alive = false;
    };
  }, [getToken, request]);

  useEffect(() => release, []);

  const close = useCallback(() => {
    setRequest(null);
    setLoaded(null);
    release();
  }, []);

  const download = useCallback(async () => {
    if (!request) return;
    try {
      await downloadFile(await getToken(), request.path, request.name);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [getToken, request]);

  const value = useMemo<Viewer>(() => ({ openDocument: setRequest }), []);

  return (
    <ViewerContext.Provider value={value}>
      {children}
      <Modal
        opened={request !== null}
        onClose={close}
        title={
          <Group gap="sm" wrap="nowrap">
            <Text fw={600} truncate maw="60vw">
              {request?.name}
            </Text>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconDownload size={14} />}
              onClick={() => void download()}
            >
              Download
            </Button>
          </Group>
        }
        size="90%"
        centered
        data-testid="document-viewer"
      >
        {error ? (
          <Alert color="red" title="Could not show the document">
            {error}
          </Alert>
        ) : !loaded ? (
          <Center h="60vh">
            <Loader aria-label="Loading document" />
          </Center>
        ) : loaded.kind === "pdf" ? (
          <iframe
            title={request?.name ?? "Document"}
            src={`${loaded.url}${request?.fragment ?? ""}`}
            style={{ width: "100%", height: "78vh", border: 0 }}
          />
        ) : loaded.kind === "image" ? (
          <Center>
            <img
              src={loaded.url}
              alt={request?.name ?? "Document"}
              style={{ maxWidth: "100%", maxHeight: "78vh" }}
            />
          </Center>
        ) : loaded.kind === "text" ? (
          <ScrollArea h="78vh">
            <Text component="pre" size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {loaded.text}
            </Text>
          </ScrollArea>
        ) : (
          <Stack align="center" gap="sm" py="xl">
            <Text>This file type can’t be shown in the browser.</Text>
            <Button leftSection={<IconDownload size={16} />} onClick={() => void download()}>
              Download to open it
            </Button>
          </Stack>
        )}
      </Modal>
    </ViewerContext.Provider>
  );
}

/** `null` outside the provider (e.g. isolated component tests). */
export function useDocumentViewer(): Viewer | null {
  return useContext(ViewerContext);
}
